/*
 * Apple Pay JS that deals with the creation of the button
 * and its actions.
 */

/*
 * The Apple Pay versions that we want to support.
 * See: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778014-supportsversion
 */
export const LOWEST_VERSION = 2;
export const HIGHEST_VERSION = 6;

const tabableTagNames = ['BUTTON', 'A'];

export const setupSingleButtonScreen = async (
  { cancellationRedirectUrl, redirectUrl, merchantId, paymentProcessArguments },
  retry = false,
) => {
  const containerElement = document.getElementById('apple-pay-container');
  /*
   * Reset visibility state if we are retrying to set the checkout button.
   * Used when showing the setup button to the user.
   */
  if (retry) {
    toggleVisibility(['apple-pay-setup-button', 'apple-pay-placeholder-spinner']);
    toggleVisibility(['apple-pay-error'], true);
    containerElement.setAttribute('aria-busy', true);
  }

  /*
   * If the customer landed here for some reason (the merchant didn't verify properly they had ApplePay),
   * we need to redirect them back.
   */
  if (!window.ApplePaySession || !ApplePaySession.canMakePayments()) {
    // Don't use .toggle so that "IE users?" can still see the popup
    document.getElementById('apple-pay-unavailable-container').classList.remove('is-hidden');
    containerElement.classList.add('is-hidden');
    containerElement.setAttribute('aria-hidden', true);

    setTimeout(() => {
      window.location.href = cancellationRedirectUrl;
    }, 4000);
    return;
  }

  /*
   * If the customer has an active card with ApplePay, show the checkout button.
   * Otherwise, show the setup button.
   * That setup button sends the user to the settings. We add a listener to recheck if there's
   * an active card when they come back.
   */
  const hasActiveCard = isiOS18version() ? await ApplePaySession.canMakePayments() : await ApplePaySession.canMakePaymentsWithActiveCard(merchantId);
  containerElement.removeAttribute('aria-busy');
  datadogLogging();

  if (hasActiveCard) {
    document
      .getElementById('apple-pay-button')
      .addEventListener('click', () => initPaymentProcess(paymentProcessArguments));
    toggleVisibility(['apple-pay-placeholder-spinner', 'apple-pay-button']);
    // We don't need this callback because the card is already setup
    window.onfocus = null;
    return;
  } else if (!retry) {
    let handlingSetupButtonClick = false;

    document
      .getElementById('apple-pay-setup-button')
      .addEventListener('click', () => {
        if (handlingSetupButtonClick) return;

        handlingSetupButtonClick = true;
        handleSetupButtonClick(merchantId).finally(() => handlingSetupButtonClick = false);
      });

    window.onfocus = () => {
      setupSingleButtonScreen({ redirectUrl, merchantId, paymentProcessArguments }, true);
    };
  }

  toggleVisibility(['apple-pay-placeholder-spinner', 'apple-pay-setup-button']);
};

/**
 * Setup the ApplePay Button in the select-method/:token page.
 */
export const setupSelectMethodScreen = async ({ merchantId, paymentProcessArguments }) => {
  if (!window.ApplePaySession) {
    return;
  }

  /*
   * Reserve space and show the loading spinner.
   * If after a few ms, ApplePay is not available in the device (up to date Safari, but non-supported device),
   * we show the unavailable message.
   *
   * We show the same unavailable message if there's no active card (we don't want a setup button in this page)
   */
  toggleVisibility(['apple-pay-container']);
  const containerElement = document.getElementById('apple-pay-container');

  if (!ApplePaySession.canMakePayments()) {
    toggleVisibility(['apple-pay-placeholder-spinner', 'apple-pay-unavailable-message']);
    containerElement.removeAttribute('aria-busy');
    return;
  }

  const hasActiveCard = isiOS18version() ? await ApplePaySession.canMakePayments() : await ApplePaySession.canMakePaymentsWithActiveCard(merchantId);
  containerElement.removeAttribute('aria-busy');

  if (hasActiveCard) {
    document
      .getElementById('apple-pay-button')
      .addEventListener('click', () => initPaymentProcess(paymentProcessArguments));
    toggleVisibility(['apple-pay-placeholder-spinner', 'apple-pay-button']);
    datadogLogging();
    return;
  }
  toggleVisibility(['apple-pay-placeholder-spinner', 'apple-pay-unavailable-message']);
  return;
};

/**
 * The setup button opens the settings with the ApplePay API,
 */
const handleSetupButtonClick = async (merchantId) => {
  toggleVisibility(['apple-pay-error'], true);

  const paymentSetupDefined = ApplePaySession.openPaymentSetup !== undefined;
  const paymentSetupOpened = paymentSetupDefined && (await ApplePaySession.openPaymentSetup(merchantId));

  if (!paymentSetupOpened) {
    toggleVisibility(['apple-pay-error']);

    if (typeof Sentry !== 'undefined') {
      Sentry.captureException(new Error('ApplePaySession.openPaymentSetup failed to open the setup page'));
    }
  }
};

/**
 * Starts a new ApplePaySession in the browser and begins the process.
 * The ApplePaySession is initiated with a version and a paySessionRequest that is generated with PaymentSheetGenerator::class
 * Sets the callbacks that will be triggered along the payment process:
 * - onvalidatemerchant will be triggered whenever the device validates the user.
 * - onpaymentauthorized will be triggered whenever the customer identifies some of the possible authorization paths.
 */
export const initPaymentProcess = ({ paySessionRequest, validationUrl, processPaymentUrl }) => {
  // Reset displaying error messages
  toggleVisibility(['apple-pay-error'], true);

  // Get the highest possible version for this user
  const supportedVersion = getSupportedVersion();
  if (!supportedVersion) {
    toggleVisibility(['apple-pay-button', 'apple-pay-unsupported-message']);
    return;
  }

  // Start the session with a clean array of supported networks
  const session = new ApplePaySession(supportedVersion, {
    ...paySessionRequest,
    supportedNetworks: cleanupSupportedNetworksArrayForApplePayVersion(
      supportedVersion,
      paySessionRequest.supportedNetworks,
    ),
  });
  addRumEvent('session-created');

  const tokenElement = document.querySelector('input[name=_formtoken]');
  if (!tokenElement || !tokenElement.value) {
    toggleVisibility(['apple-pay-error']);
    addRumError('token-element-not-found');
    return session.abort();
  }
  session.onvalidatemerchant = event =>
    validateMerchant(event, session, tokenElement, validationUrl);

  session.onpaymentauthorized = paymentAuthorizedEvent =>
    authorizePayment(paymentAuthorizedEvent, session, tokenElement, processPaymentUrl);

  return session.begin();
};

/**
 * First step for the ApplePay Process.
 * The callback is triggered after the Device validates the customers' data.
 * The ApplePaySession provides us with a URL that we send to the platform.
 * The platform generates a merchantSession with Apple's servers and returns it.
 * We use that merchantSession to start the authorization process.
 */

export const validateMerchant = async (event, session, tokenElement, validationUrl) => {
  if (event && event.validationURL) {
    const merchantSession = await fetch(validationUrl, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': tokenElement.value,
      },
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        validationUrl: event.validationURL,
      }),
    });
    if (merchantSession.status < 400 && merchantSession.ok) {
      // This will trigger onpaymentauthorized after the user accepts in their device.
      const response = await merchantSession.json();
      addRumEvent('merchant-validated');
      return session.completeMerchantValidation(response);
    }
    addRumError('merchant-validation-failed');
    toggleVisibility(['apple-pay-error']);
    return session.abort();
  }
  toggleVisibility(['apple-pay-error']);
  return session.abort();
};

/**
 * Second step of the ApplePay process.
 * The callback is triggered when the customer authorises a payment.
 * We get a paymentAuthorizedEvent from Apple and we send it to the platform with the CSRF token.
 * We receive a success response with some errors if present.
 * We close the ApplePay modal with that response and add the errors to the page if present.
 */

export const authorizePayment = async (
  paymentAuthorizedEvent,
  session,
  tokenElement,
  processPaymentUrl,
) => {
  if (
    !paymentAuthorizedEvent ||
    !paymentAuthorizedEvent.payment ||
    !paymentAuthorizedEvent.payment.token
  ) {
    toggleVisibility(['apple-pay-error']);
    addRumError('authorization-failed');
    return session.completePayment({
      status: ApplePaySession.STATUS_FAILURE,
    });
  }

  addRumEvent('authorize-payment-triggered');

  // Send this token + CSRF on an XHR call to platform, wait for response.
  const paymentResult = await fetch(processPaymentUrl, {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': tokenElement.value,
    },
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(paymentAuthorizedEvent.payment),
  });

  if (paymentResult.status < 400 && paymentResult.ok) {
    // This will trigger onpaymentauthorized after the user accepts in their device.
    const response = await paymentResult.json();
    const { redirectUrl, status } = response;
    if (status === 'paid' || status === 'authorized') {
      addRumEvent('payment-paid');
      try {
        session.completePayment({
          status: ApplePaySession.STATUS_SUCCESS
        });
      } catch (error) {
        addRumError('complete-payment-failed');
      }


      if (redirectUrl) {
        // We have to wait a bit here, because Safari (at least on Mojave, High Sierra) crashes if we redirect immediately.
        // While waiting we're showing a spinner here.
        toggleVisibility([
          'apple-pay-success-spinner',
          'apple-pay-container',
          'payment-method-list',
        ]);
        await new Promise(resolve => setTimeout(resolve, 2500));
        addRumEvent('redirect-triggered');
        return (window.location = redirectUrl);
      }
    }
  }

  toggleVisibility(['apple-pay-error']);

  return session.completePayment({
    status: ApplePaySession.STATUS_FAILURE,
  });
};

/*
 * Get the highest version that is supported by the OS
 */
export const getSupportedVersion = () => {
  let i = HIGHEST_VERSION;
  let supportedVersion = null;

  while (!supportedVersion && i >= LOWEST_VERSION) {
    if (ApplePaySession.supportsVersion(i)) {
      supportedVersion = i;
    }

    i--;
  }

  return supportedVersion;
};

/*
 * Every Apple Pay version comes with a list of supportedNetworks.
 * We should define this list based on the highest supported version for the device ourself, because Apple Pay otherwise crashes.
 * See: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778014-supportsversion
 * See: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916122-supportednetworks
 */
export const cleanupSupportedNetworksArrayForApplePayVersion = (version, supportedByMollie) => {
  const supportedNetworks = [...supportedByMollie];

  if (version < 4) {
    // Throw away Maestro and vPay
    supportedNetworks.splice(supportedNetworks.indexOf('maestro'), 1);
    supportedNetworks.splice(supportedNetworks.indexOf('vPay'), 1);
  }

  return supportedNetworks;
};

const toggleVisibility = (elementIds, reset) => {
  if (!elementIds || elementIds.length <= 0) {
    return;
  }
  elementIds.forEach(element => {
    const domElement = document.getElementById(element);

    if (!domElement) {
      return;
    }

    if (reset) {
      domElement.classList.add('is-hidden');
      domElement.setAttribute('aria-hidden', true);

      if (tabableTagNames.includes(domElement.tagName)) {
        domElement.tabIndex = -1;
      }

      return;
    }

    domElement.classList.toggle('is-hidden');

    if (domElement.hasAttribute('aria-hidden')) {
      domElement.removeAttribute('aria-hidden');

      if (tabableTagNames.includes(domElement.tagName)) {
        domElement.tabIndex = 0;
      }
      return;
    }

    domElement.setAttribute('aria-hidden', true);

    if (tabableTagNames.includes(domElement.tagName)) {
      domElement.tabIndex = -1;
    }
  });
};

function isiOS18version() {
  if (/iPad|iPhone/.test(navigator.userAgent) && !window.MSStream) {
    const [, major = 0] = (navigator.userAgent.match(/OS (\d+)/) || []);
    return parseInt(major, 10) >= 18;
  }

  return false;
}

function addRumEvent(eventName) {
  if (typeof DD_RUM == 'undefined' || !DD_RUM.addAction) {
    return;
  }

  DD_RUM.addAction('apple-pay-monitoring', { eventName });
}

function addRumError(eventName) {
  if (typeof DD_RUM == 'undefined' || !DD_RUM.addError) {
    return;
  }

  DD_RUM.addError('apple-pay-monitoring', { eventName });
}

function datadogLogging() {
  document.addEventListener("dataDogReady",() => {
    if (DD_RUM && !!DD_RUM.addAction) {
      DD_RUM.addAction('wallet-button-loaded', {
        'type': 'applePay'
      });
    }
  });
}

